import React from "react";
import { Helmet } from "react-helmet";
import {
  Container,
  Grid,
  Avatar,
  Box,
  Paper,
  Typography,
  useTheme,
} from "@mui/material/";
import CustomAppBar from "../../components/AppBar";
import { useNavigate } from "react-router-dom";
import { CustomTitle } from "../../components/Custom/CustomTypography";
import { useTranslation } from "react-i18next";
import LoaderContainer from "../../components/LoaderContainer";
import PersonIcon from "@mui/icons-material/Person";
import Subscriptions from "@mui/icons-material/Subscriptions";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { getAuth } from "../../globals/authentication";

export default function FarmerProfile() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const auth = getAuth() || {};
  const { user = {} } = auth;

  const sections = [
    {
      icon: <PersonIcon color="primary" />,
      label: t("Personal Information"),
      route: "/farmer/profile/personal",
      description: t("Manage your personal details"),
    },
    {
      icon: <Subscriptions color="primary" />,
      label: t("My Invoices"),
      route: "/my-invoices",
      description: t("View your invoices"),
    },
    // {
    //   icon: <Subscriptions color="primary" />,
    //   label: t("My Subscriptions"),
    //   route: "/my-subscriptions",
    //   description: t("View your active subscriptions"),
    // },
  ];

  const handleSectionClick = (route) => {
    navigate(route);
  };

  return (
    <>
      <Helmet>
        <title>Profile - Farmapp</title>
      </Helmet>
      <CustomAppBar />
      <LoaderContainer>
        <Container maxWidth="xs">
          <Box sx={{ my: 3 }}>
            <CustomTitle>{t("Profile")}</CustomTitle>
          </Box>

          {/* Enhanced Profile Header */}
          <Paper
            elevation={3}
            sx={{
              p: 3.5,
              mb: 4,
              borderRadius: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
              color: "white",
              transition: "transform 0.3s ease, box-shadow 0.3s ease",
              "&:hover": {
                boxShadow: 6,
                transform: "translateY(-4px)",
              },
            }}
          >
            <Avatar
              sx={{
                width: 100,
                height: 100,
                bgcolor: "white",
                color: theme.palette.primary.main,
                fontSize: 36,
                fontWeight: "bold",
                mb: 2.5,
                boxShadow: "0px 6px 15px rgba(0, 0, 0, 0.15)",
                border: "4px solid white",
                transition: "transform 0.3s ease",
                "&:hover": {
                  transform: "scale(1.05)",
                },
              }}
            >
              {user?.app_user?.full_name
                ? user.app_user.full_name[0].toUpperCase()
                : "F"}
            </Avatar>
            <Typography variant="h5" fontWeight="700" sx={{ mb: 1 }}>
              {user?.app_user?.full_name || t("Farmer")}
            </Typography>
            {user?.app_user?.phone_number && (
              <Typography sx={{ opacity: 0.85 }}>
                <b>{t("Mobile")}</b>: {user.app_user.phone_number}
              </Typography>
            )}
            {user?.email && (
              <Typography sx={{ opacity: 0.85 }}>
                <b>{t("Email")}</b>: {user.email}
              </Typography>
            )}
          </Paper>

          <Typography
            variant="subtitle1"
            fontWeight="600"
            sx={{ mb: 2, ml: 1 }}
          >
            {t("Account Settings")}
          </Typography>

          <Box sx={{ mb: 4 }}>
            {sections.map((section, index) => (
              <Box
                key={index}
                sx={{
                  mb: 2.5,
                  transition: "transform 0.2s ease",
                  "&:hover": {
                    transform: "scale(1.02)",
                  },
                  "&:active": {
                    transform: "scale(0.98)",
                  },
                }}
              >
                <Paper
                  elevation={2}
                  onClick={() => handleSectionClick(section.route)}
                  sx={{
                    p: 2.5,
                    borderRadius: 3,
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: 5,
                      bgcolor: "rgba(25, 118, 210, 0.04)",
                    },
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid item>
                      <Box
                        sx={{
                          p: 1.8,
                          borderRadius: 2.5,
                          background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                        }}
                      >
                        {React.cloneElement(section.icon, {
                          sx: { color: "white" },
                        })}
                      </Box>
                    </Grid>
                    <Grid item xs>
                      <Typography variant="subtitle1" fontWeight="600">
                        {section.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {section.description}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <ArrowForwardIosIcon
                        fontSize="small"
                        sx={{ color: theme.palette.primary.main, fontSize: 16 }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
            ))}
          </Box>
        </Container>
      </LoaderContainer>
    </>
  );
}
